/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import dayjs from "dayjs";
import {
  DateRangePicker,
  useLocalization,
  type DateRange,
} from "@lemmonhq/uikit";
import { ALL_RESERVATION_STATUSES } from "@lemmonhq/types";

export const RESERVATION_SOURCES = ["online", "phone"] as const;

export type ReservationFilterState = {
  dateRange: DateRange;
  statuses: string[];
  sources: string[];
};

/**
 * Chips are 32px tall minimum — the iPad is the primary device and this is
 * the design system's floor for a touch target.
 */
export const chipClass = (active: boolean) =>
  [
    // h-8 (32px) rather than min-h: an explicit height cannot be lost to a
    // competing padding or line-height rule the way a minimum can.
    "inline-flex h-8 items-center rounded-full border px-3 text-xs font-medium transition-colors",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
    active
      ? "border-primary bg-primary text-primary-content shadow-sm"
      : " bg-base-100 text-base-content hover:border-primary hover:bg-primary/10 hover:text-primary",
  ].join(" ");

const filterLabelClass =
  "text-[11px] font-semibold uppercase tracking-wider text-base-content/75";

/** Same keys the picker itself labels its presets with. */
const DATE_PRESET_KEYS: Record<string, string> = {
  today: "date_range_picker.options.today",
  yesterday: "date_range_picker.options.yesterday",
  this_week: "date_range_picker.options.this_week",
  last_week: "date_range_picker.options.last_week",
  this_month: "date_range_picker.options.this_month",
  last_month: "date_range_picker.options.last_month",
  this_quarter: "date_range_picker.options.this_quarter",
  last_quarter: "date_range_picker.options.last_quarter",
  this_year: "date_range_picker.options.this_year",
  last_year: "date_range_picker.options.last_year",
  last_12m: "date_range_picker.options.last_12_months",
};

export const statusLabelKey = (status: string) =>
  `reports.reservations.status.${status.replace(/-/g, "_")}`;

/** Chips and the date picker are behind a sheet on mobile, so the header line
 *  below the title is the only place the active period is visible there. */
export function buildFilterSummary(
  filters: ReservationFilterState,
  t: (key: string) => string,
): string {
  const { dateRange } = filters;
  const dateLabel =
    dateRange.type === "custom"
      ? (() => {
          const start = dayjs(dateRange.startDate).format("MMM D");
          const end = dayjs(dateRange.endDate).format("MMM D, 'YY");
          return start === dayjs(dateRange.endDate).format("MMM D")
            ? end
            : `${start} – ${end}`;
        })()
      : t(DATE_PRESET_KEYS[dateRange.type] ?? dateRange.type);

  return [
    dateLabel,
    ...filters.statuses.map((status) => t(statusLabelKey(status))),
    ...filters.sources.map((source) =>
      t(`reports.reservations.source.${source}`),
    ),
  ].join(" · ");
}

/** Date range is excluded: it always has a value, so it is never "active". */
export function countActiveFilters(filters: ReservationFilterState): number {
  return filters.statuses.length + filters.sources.length;
}

const toggleValue = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

type ChipRowsProps = {
  filters: ReservationFilterState;
  onFiltersChange: (filters: ReservationFilterState) => void;
  /** Stacked in the mobile sheet, inline on the desktop panel. */
  stacked?: boolean;
};

/**
 * Statuses and sources are closed vocabularies, so they are chips rather than
 * free text — every value here can return rows.
 */
export const ReservationChipRows = defineComponent<ChipRowsProps>({
  name: "ReservationChipRows",
  props: ["filters", "onFiltersChange", "stacked"],
  setup(props) {
    const { t } = useLocalization();

    const rowClass = () =>
      props.stacked
        ? "flex flex-col gap-1.5"
        : "flex flex-wrap items-center gap-x-2 gap-y-1.5";

    return () => (
      <>
        <div class={rowClass()}>
          <span class={props.stacked ? filterLabelClass : `mr-1 ${filterLabelClass}`}>
            {t("reports.reservations.filter.status")}
          </span>
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {ALL_RESERVATION_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={props.filters.statuses.includes(status)}
                class={chipClass(props.filters.statuses.includes(status))}
                onClick={() =>
                  props.onFiltersChange({
                    ...props.filters,
                    statuses: toggleValue(props.filters.statuses, status),
                  })
                }
              >
                {t(statusLabelKey(status))}
              </button>
            ))}
          </div>
        </div>

        <div class={rowClass()}>
          <span class={props.stacked ? filterLabelClass : `mr-1 ${filterLabelClass}`}>
            {t("reports.reservations.filter.source")}
          </span>
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {RESERVATION_SOURCES.map((source) => (
              <button
                key={source}
                type="button"
                aria-pressed={props.filters.sources.includes(source)}
                class={chipClass(props.filters.sources.includes(source))}
                onClick={() =>
                  props.onFiltersChange({
                    ...props.filters,
                    sources: toggleValue(props.filters.sources, source),
                  })
                }
              >
                {t(`reports.reservations.source.${source}`)}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  },
});

type PanelProps = {
  filters: ReservationFilterState;
  onFiltersChange: (filters: ReservationFilterState) => void;
};

/**
 * Desktop-only disclosure panel. It sits under the header rather than in the
 * header itself: two rows of chips need the full page width, and they are set
 * once and then left alone, unlike the date range.
 */
export const ReservationFilterPanel = defineComponent<PanelProps>({
  name: "ReservationFilterPanel",
  props: ["filters", "onFiltersChange"],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <section class="flex min-w-0 flex-col gap-3 rounded-2xl border bg-base-200 p-3 shadow-sm sm:p-4">
        <ReservationChipRows
          filters={props.filters}
          onFiltersChange={props.onFiltersChange}
        />
        {countActiveFilters(props.filters) > 0 && (
          <div class="flex justify-end border-t pt-2">
            <button
              type="button"
              class="btn btn-ghost btn-xs gap-1 text-base-content/80 hover:text-error"
              onClick={() =>
                props.onFiltersChange({
                  ...props.filters,
                  statuses: [],
                  sources: [],
                })
              }
            >
              <span class="material-symbols-outlined text-sm">
                filter_alt_off
              </span>
              {t("reports.reservations.filters_reset")}
            </button>
          </div>
        )}
      </section>
    );
  },
});

type ReservationFiltersProps = {
  filters: ReservationFilterState;
  onFiltersChange: (filters: ReservationFilterState) => void;
  /** Desktop chip panel visibility, owned by the page that renders the panel. */
  panelOpen: boolean;
  onTogglePanel: () => void;
  /** Called after Apply is tapped on mobile — lets PageHeader close the sheet. */
  onApply?: () => void;
};

/**
 * Filter controls for the reservations report.
 *
 * Desktop: date range picker plus the chip-panel toggle, inline in the header.
 * Mobile: everything inside PageHeader's BottomSheet, committed on Apply —
 * the header there only has room for the title and the Filter button.
 */
export const ReservationFilters = defineComponent<ReservationFiltersProps>({
  name: "ReservationFilters",
  props: [
    "filters",
    "onFiltersChange",
    "panelOpen",
    "onTogglePanel",
    "onApply",
  ],
  setup(props) {
    const { t } = useLocalization();

    // Pending state — committed only when Apply is pressed (mobile).
    const pending = ref<ReservationFilterState>({ ...props.filters });

    const applySheet = () => {
      props.onFiltersChange({ ...pending.value });
      props.onApply?.();
    };

    const clearSheet = () => {
      pending.value = { ...pending.value, statuses: [], sources: [] };
      props.onFiltersChange({ ...pending.value });
      props.onApply?.();
    };

    const activeCount = computed(() => countActiveFilters(props.filters));
    const pendingCount = computed(() => countActiveFilters(pending.value));

    // ── Desktop (inline in PageHeader) ───────────────────────────────────────
    const renderDesktop = () => (
      <>
        <DateRangePicker
          value={props.filters.dateRange}
          onChange={(dateRange) =>
            props.onFiltersChange({ ...props.filters, dateRange })
          }
        />
        <button
          type="button"
          class={[
            "btn btn-secondary gap-1.5 font-normal",
            activeCount.value > 0 ? "border-primary text-primary" : "",
          ].join(" ")}
          aria-expanded={props.panelOpen}
          onClick={props.onTogglePanel}
        >
          <span class="material-symbols-outlined text-base">tune</span>
          {t("reports.reservations.filters_toggle")}
          {activeCount.value > 0 && (
            <span class="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-content">
              {activeCount.value}
            </span>
          )}
        </button>
      </>
    );

    // ── Mobile sheet content (rendered inside PageHeader's BottomSheet) ──────
    const renderMobileSheet = () => (
      <div class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-1.5">
          <label class={filterLabelClass}>
            {t("reports.reservations.filters_date_range")}
          </label>
          <DateRangePicker
            value={pending.value.dateRange}
            onChange={(dateRange) =>
              (pending.value = { ...pending.value, dateRange })
            }
          />
        </div>

        <ReservationChipRows
          filters={pending.value}
          onFiltersChange={(next) => (pending.value = next)}
          stacked={true}
        />

        <div class="flex gap-2 pt-1">
          <button class="btn btn-primary btn-sm flex-1" onClick={applySheet}>
            <span class="material-symbols-outlined text-base">check</span>
            {t("reports.reservations.filters_apply")}
            {pendingCount.value > 0 && (
              <span class="badge badge-sm badge-primary-content ml-1">
                {pendingCount.value}
              </span>
            )}
          </button>
          {pendingCount.value > 0 && (
            <button class="btn btn-ghost btn-sm text-error" onClick={clearSheet}>
              {t("reports.reservations.filters_reset")}
            </button>
          )}
        </div>
      </div>
    );

    return () => (
      <>
        {/* PageHeader shows this on desktop (md:block wrapper), hides on mobile */}
        <div class="hidden md:flex items-center gap-2 flex-wrap">
          {renderDesktop()}
        </div>
        {/* PageHeader shows this in the BottomSheet on mobile */}
        <div class="md:hidden">{renderMobileSheet()}</div>
      </>
    );
  },
});
